import { useMemo, useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import Header from "../../components/Header";
import Menu from "../../components/Menu";
import Form from "../../../../../components/shared/Form";
import Table from "../../components/Table";
import Filters from "../components/Filters";
import Settings from "./Settings";
import { useColumns, useHiddenColumns } from "../hooks/useTimesheetColumns";
import { useFilters } from "./useFilters";
import { useCompanyPreferences } from "../../hooks/usePreferences";
import { usePreferenceMutation } from "../../api/usePreferenceMutation";
import { buildColumnsValue, buildFiltersValue } from "../helpers";
import type { MyTimesheetFormValues } from "./types";

const options: string[] = ["columns", "filters", "settings"];

function My() {
    const [option, setOption] = useState<string>("columns");

    const columns = useColumns();
    const hiddenColumns = useHiddenColumns();
    const filters = useFilters();

    const config = useCompanyPreferences("myTimesheet");
    const { mutate, isPending } = usePreferenceMutation("company", "myTimesheet");

    const defaultColumnsValue = useMemo(() => {
        return buildColumnsValue(columns, hiddenColumns, config?.columns, { canSort: false });
    }, [columns, hiddenColumns, config]);

    const defaultFiltersValue = useMemo(() => {
        return buildFiltersValue(filters, config?.filters);
    }, [filters, config]);

    const formDefaults = useMemo<MyTimesheetFormValues>(() => ({
        columns: defaultColumnsValue,
        filters: defaultFiltersValue,
        defaultFilter: config?.defaultFilter || "",
        mandatoryNoteOnEdit: config?.mandatoryNoteOnEdit || false,
        mandatoryNoteOnCreate: config?.mandatoryNoteOnCreate || false,
        hideDailySummary: config?.hideDailySummary || false,
        hideWeeklySummary: config?.hideWeeklySummary || false,
        timeSheet: {
            hideClockDatePicker: config?.timeSheet?.hideClockDatePicker || false,
        },
    }), [defaultColumnsValue, defaultFiltersValue, config]);

    const form = useForm<MyTimesheetFormValues>({
        defaultValues: formDefaults,
        values: formDefaults,
        resetOptions: { keepDirtyValues: true },
    });

    const onFinish = (value: MyTimesheetFormValues) => mutate({
        ...config,
        ...value,
        timeSheet: { ...config?.timeSheet, ...value.timeSheet },
    });

    const panels: { key: string; node: ReactNode }[] = [
        { key: "columns", node: <Table columns={columns} fieldName="columns" canSort={false} /> },
        { key: "filters", node: <Filters config={filters} /> },
        { key: "settings", node: <Settings /> },
    ];

    return (
        <div className="tw-p-5 tw-h-full tw-bg-white tw-rounded-lg">
            <Form className="tw-h-full" form={form} onFinish={onFinish}>
                <Header name="my-timesheet" isLoading={isPending} />
                <Menu
                    options={options}
                    option={option}
                    setOption={setOption}
                />

                <div className="tw-px-7" style={{ height: `calc(100% - 200px)` }}>
                    {panels.map(({ key, node }) => (
                        <div
                            key={key}
                            className="tw-h-full"
                            style={{ display: option === key ? undefined : "none" }}
                        >
                            {node}
                        </div>
                    ))}
                </div>
            </Form>
        </div>
    );
}

export default My;
